import {gql, useQuery, OperationVariables} from '@apollo/client';
import {Customer, Dictionary, Grade, Program, SelectOption} from '../types';
import {useMemo} from 'react';

type Dictionaries =
    'ProgramTypes'
    | 'Grades'
    | 'Programs'
    | 'Customers'
    | 'Roles'
    | 'Countries'
    | 'Positions'
    | 'SurveyTypes'
    | 'CustomerSchools'
    | 'Schools'
    | 'AdminUsers'
    | 'Skills'
    | 'SllPrograms';

type Params = {
  dictionary: Dictionaries;
  isSkip?: boolean;
  variables?: OperationVariables;
}

const programsQuery = gql`
  query Programs {
    allPrograms {
      id
      name
      grade {
        position
      }
      country
    }
  }
`;

const gradesQuery = gql`
  query Grades {
    grades {
      id
      name
      position
    }
  }
`;

const programTypesQuery = gql`
  query ProgramTypes {
    programTypes {
      id
      name
    }
  }
`;

const customersQuery = gql`
  query Customers {
    customers {
      id
      name
      trial
    }
  }
`;

const rolesQuery = gql`
  query Roles {
    roles {
      id
      name
    }
  }
`;

const positionQuery = gql`
  query Positions {
    positions {
      id
      name
    }
  }
`;

const surveyTypesQuery = gql`
  query SurveyTypes {
    surveyTypes {
      id, name
    }
  }
`;

const schoolsQuery = gql`
  query Schools {
    schools {
      id, name
    }
  }
`;

const customerSchoolsQuery = gql`
  query getAccountSchools($input: UserIDInput!) {
    accountSchools(input: $input) {
      schools {
        name
        id
      }
    }
  }
`;

const adminUsersQuery = gql`
  query AdminUsers {
    adminUsers {
      id
      name
    }
  }
`;

const skillsQuery = gql`
  query Skills {
    skills {
      id
      name
    }
  }
`;

const sllProgramsQuery = gql`
  query SllPrograms {
    getPrograms {
      id
      name
      country
    }
  }
`;

type GradesResponse = {
  grades: Grade[];
};

type ProgramsResponse = {
  allPrograms: Program[];
};

type ProgramTypesResponse = {
  programTypes: Dictionary[];
};

type CustomersResponse = {
  customers: Customer[];
};

type RolesResponse = {
  roles: Dictionary[];
};

// type CountriesResponse = {
//   countries: Dictionary[];
// };

type PositionResponse = {
  positions: Dictionary[];
}

type SurveyTypesResponse = {
  surveyTypes: Dictionary[];
}

type SchoolsResponse = {
  schools: Dictionary[];
}

type CustomerSchoolsResponse = {
  accountSchools: { schools: Dictionary[] };
}

type AdminUsersResponse = {
  adminUsers: Dictionary[];
}

type SkillsResponse = {
  skills: Dictionary[];
}

type SllProgramsResponse = {
  getPrograms: {
    id: string;
    name: string;
    country: string;
  }[];
}

type ResponseType = ProgramsResponse |
  GradesResponse |
  ProgramTypesResponse |
  CustomersResponse |
  RolesResponse |
  // CountriesResponse |
  PositionResponse |
  SurveyTypesResponse |
  SchoolsResponse |
  AdminUsersResponse |
  CustomerSchoolsResponse |
  SkillsResponse |
  SllProgramsResponse;

const getQuery = (dictionary: Dictionaries) => {
  switch (dictionary) {
    case 'Grades': {
      return gradesQuery;
    }
    case 'Programs': {
      return programsQuery;
    }
    case 'ProgramTypes': {
      return programTypesQuery;
    }
    case 'Customers': {
      return customersQuery;
    }
    case 'Roles': {
      return rolesQuery;
    }
    case 'Positions': {
      return positionQuery;
    }
    case 'SurveyTypes': {
      return surveyTypesQuery;
    }
    case 'Schools': {
      return schoolsQuery;
    }
    case 'CustomerSchools': {
      return customerSchoolsQuery;
    }
    case 'AdminUsers': {
      return adminUsersQuery;
    }
    case 'Skills': {
      return skillsQuery;
    }
    case 'SllPrograms': {
      return sllProgramsQuery;
    }
  }

  return programsQuery;
}

const mapToOptions = (d: { id: string, name: string }): SelectOption => ({
  value: d.id,
  label: d.name
});

const getResponse = (dictionary: Dictionaries, data: ResponseType): SelectOption[] => {
  switch (dictionary) {
    case 'ProgramTypes': {
      return (data as ProgramTypesResponse).programTypes.map(mapToOptions);
    }
    case 'Grades': {
      return [...(data as GradesResponse).grades].sort((a, b) => a.position - b.position).map(mapToOptions);
    }
    case 'Programs': {
      return [...(data as ProgramsResponse).allPrograms].sort((a, b) => (a?.grade?.position || 0) - (b?.grade?.position || 0)).map(mapToOptions);
    }
    case 'Customers': {
      return (data as CustomersResponse).customers.map(n => ({
        value: n.id,
        label: n.name,
        trial: n.trial
      }));
    }
    case 'Roles': {
      return (data as RolesResponse).roles.map(mapToOptions);
    }
    case 'Positions': {
      return (data as PositionResponse).positions.map(mapToOptions);
    }
    case 'SurveyTypes': {
      return (data as SurveyTypesResponse).surveyTypes.map(mapToOptions);
    }
    case 'Schools': {
      return (data as SchoolsResponse).schools.map(mapToOptions);
    }
    case 'CustomerSchools': {
      return (data as CustomerSchoolsResponse).accountSchools.schools.map(mapToOptions);
    }
    case 'AdminUsers': {
      return (data as AdminUsersResponse).adminUsers.map(mapToOptions);
    }
    case 'Skills': {
      return (data as SkillsResponse).skills.map(mapToOptions);
    }
  }

  return []
}

export function useQueryDictionary(params: Params) {
  const {dictionary, isSkip, variables} = params;
  const {data} = useQuery<ResponseType>(getQuery(dictionary), { fetchPolicy: 'cache-first', skip: isSkip, variables });
  return useMemo(() => {
    if (!data) return [];
    return getResponse(dictionary, data);
  }, [dictionary, data]);
}

export function useQueryProgramWithCountry() {
  const {data, loading} = useQuery<ResponseType>(getQuery('Programs'), { fetchPolicy: 'cache-first' });
  return useMemo(() => {
    if (!data) return {
      data: [], loading
    };
    return {
      data: [...(data as ProgramsResponse).allPrograms].map((program) => {
        const programCountry: SelectOption = {
          label: program.id,
          value: program.country ?? ""
        }
        return programCountry
      }), 
      loading
    };
    
  }, [data, loading]);
}

export function useQuerySllProgramWithCountry() {
  const {data} = useQuery<ResponseType>(getQuery('SllPrograms'), { fetchPolicy: 'cache-first' });
  return useMemo(() => {
    if (!data) return [];
    return (data as SllProgramsResponse).getPrograms.map(p => ({
      value: p.id,
      label: p.name,
      country: p.country
    }))
  }, [data]);
}